import React from 'react';
import { resolveMediaUrl } from '../services/api';

function formatTimestamp(asset) {
  const ts = asset?.createdAt || asset?.created_at;
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return '';
  }
}

function getVideoLabel(asset) {
  return (
    asset?.name ||
    asset?.filename ||
    asset?.metadata?.title ||
    (asset?.url ? decodeURIComponent(asset.url.split('/').pop() || '') : 'Project Video')
  );
}

function VideoCard({ video, isLatest }) {
  const videoUrl = resolveMediaUrl(video?.url || '');
  const thumbnailUrl = resolveMediaUrl(video?.thumbnailUrl || video?.thumbnail_url || video?.url || '');
  return (
    <div
      style={{
        border: '1px solid rgba(148,163,184,0.2)',
        borderRadius: 16,
        padding: 16,
        background: 'rgba(15,23,42,0.65)',
        boxShadow: '0 15px 35px rgba(15,23,42,0.35)'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600 }}>{getVideoLabel(video)}</div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>{formatTimestamp(video)}</div>
        </div>
        {isLatest ? (
          <span
            style={{
              background: 'rgba(16,185,129,0.15)',
              color: '#34d399',
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: 999
            }}
          >
            Latest
          </span>
        ) : null}
      </div>
      <div style={{ marginTop: 12 }}>
        <video
          controls
          playsInline
          preload="metadata"
          poster={thumbnailUrl}
          src={videoUrl}
          style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(148,163,184,0.3)' }}
        />
      </div>
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <a href={videoUrl} target="_blank" rel="noreferrer" style={{ color: '#93c5fd', fontSize: 13 }}>
          Open in new tab ↗
        </a>
        {Number.isFinite(Number(video?.durationMs)) ? (
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            Duration: {(Number(video.durationMs) / 1000).toFixed(1)}s
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function VideoGallery({
  title = 'Project Videos',
  videos = [],
  emptyMessage = 'No project videos yet. Generate one to kick off your campaign.',
  onRefresh,
  refreshing = false,
  onLoadMore,
  canLoadMore = false,
  loadingMore = false
}) {
  const hasRefresh = typeof onRefresh === 'function';
  return (
    <div className="section" style={{ background: 'rgba(17,24,39,0.85)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <div style={{ color: '#9ca3af', fontSize: 12 }}>
            {videos.length ? `${videos.length} video${videos.length === 1 ? '' : 's'} generated in this project.` : 'Generate a video to see it here.'}
          </div>
        </div>
        {hasRefresh ? (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            style={{
              borderRadius: 8,
              border: '1px solid rgba(148,163,184,0.4)',
              background: 'transparent',
              color: '#e2e8f0',
              padding: '6px 14px',
              fontSize: 13,
              cursor: refreshing ? 'not-allowed' : 'pointer'
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        ) : null}
      </div>
      {videos.length === 0 ? (
        <div style={{ marginTop: 16, border: '1px dashed rgba(226,232,240,0.2)', borderRadius: 12, padding: 20, color: '#9ca3af', fontSize: 13 }}>
          {emptyMessage}
        </div>
      ) : (
        <div style={{ marginTop: 16, display: 'grid', gap: 16 }}>
          {videos.map((video, index) => (
            <VideoCard key={video.id || video.assetId || video._id || `video-${index}`} video={video} isLatest={index === 0} />
          ))}
        </div>
      )}
      {canLoadMore && typeof onLoadMore === 'function' ? (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            style={{
              borderRadius: 999,
              border: '1px solid rgba(148,163,184,0.4)',
              background: 'rgba(15,23,42,0.9)',
              color: '#93c5fd',
              padding: '8px 20px',
              fontSize: 13,
              cursor: loadingMore ? 'not-allowed' : 'pointer'
            }}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
